// src/utils/entitlements.js
//
// Pure mirror of the backend's output-type vocabulary
// (`ccode/src/auth/app_catalogue_seed.py` — `_VARIANTS`, and the `variants[]`
// field of `GET /api/v1/entitlements`). No React, no network, no module
// state: it is imported by a hook (wizard locks) and by the wizard bridge,
// and both must get the same answer the server gets.
//
// ⚠️ This file exists so the FE never *offers* a format the plan will refuse.
// It is a UX pre-flight, NEVER the enforcement: the server re-runs the gate on
// the run and answers 402 FEATURE_NOT_IN_PLAN.
//
// ⛔⛔ THE KEY IS BUILT BY JOINING, NEVER RECOVERED BY SPLITTING.
// `email-email-classic` is `email` + `email-classic`, but splitting on the
// first `-` reads it as `email` + `email-classic` only by luck and as
// `email-email` + `classic` just as readily. The backend carries the same
// rule ("JOIN, never SPLIT") and publishes `variants[]` precisely so no client
// has to parse. Nothing in this file may ever split a variant key.
//
// ⛔ The previous `platform:format` / `platform:*` / bare-`platform` token
// grammar was removed from the backend on 2026-09-20 (commit 9dbbbc1) along
// with the whole `allowed_formats` field. Do not reintroduce it.

/**
 * Backend `_norm`: `str(v).strip().lower()`, with **empty string -> null**.
 *
 * The empty-string case is not defensive padding: `resolvePlatformAndFormat`'s
 * `default:` branch (`services/api/backendClient.js`) returns
 * `{ platform: activeMode, content_format: '' }`. A blank format means
 * **absent**, and an absent half yields no key at all.
 */
function norm(value) {
  if (value == null) return null;
  const text = String(value).trim().toLowerCase();
  return text || null;
}

/**
 * The `{platform}-{content_format}` key the backend prices and grants on.
 *
 * @param {string|null|undefined} platform e.g. 'linkedin'
 * @param {string|null|undefined} contentFormat e.g. 'post'; '' / null = absent
 * @returns {string|null} the joined key, or null when either half is absent —
 *   which is NOT a member of the closed vocabulary and would be refused at
 *   submit with 422 BRIEF_VALIDATION_FAILED anyway.
 */
export function variantKey(platform, contentFormat) {
  const platformN = norm(platform);
  const formatN = norm(contentFormat);
  if (platformN === null || formatN === null) return null;
  return `${platformN}-${formatN}`;
}

/**
 * Does the plan's `variants[]` sell this (platform, content_format)?
 *
 * Absence rules:
 *   - `variants == null` (not known: loading, no tenant, a failed read, a
 *     backend that predates the route) -> **allow everything**. A lookup
 *     failure must never blank the catalogue.
 *   - a non-array -> same treatment (malformed field).
 *   - `[]` -> **allow nothing**. This is the normal refusal shape: every
 *     `reason` other than OK/EXEMPT comes back 200 with an empty list.
 *   - a known list with an unbuildable key -> **refuse** (fail closed),
 *     exactly as the server refuses an unknown pair.
 *
 * Matching is case-insensitive and whitespace-trimmed on **both** sides.
 *
 * @param {string[]|null|undefined} variants plan variant keys; null/absent = unrestricted
 * @param {string|null|undefined} platform
 * @param {string|null|undefined} contentFormat
 * @returns {boolean} true when the plan sells this pair
 */
export function isVariantAllowed(variants, platform, contentFormat) {
  if (variants == null) return true;
  if (!Array.isArray(variants)) return true; // malformed field -> fail open

  const key = variantKey(platform, contentFormat);
  if (key === null) return false; // fail closed on a known list

  const tokens = new Set(
    variants
      .filter(v => v != null)
      .map(v => String(v).trim().toLowerCase())
  );
  return tokens.has(key);
}
